import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    useTheme,
    useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Chip
} from '@mui/material';
import { DateRange, AttachMoney, Receipt, BarChart, TrendingUp, TrendingDown } from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { viewCommissions } from '../../../Utils/services';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const CommissionDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loadSpinner, setLoadSpinner] = useState(false);
    const [graphLoading, setGraphLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for filters
    const [dateRange, setDateRange] = useState({
        startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd')
    });
    const [groupBy, setGroupBy] = useState('month'); // month/week/day

    // State for data
    const [summaryData, setSummaryData] = useState({
        total_number_transactions: 0,
        total_commission_amount: 0,
        total_transaction_amount: 0,
        buy_transactions: 0,
        buy_commission: 0,
        buy_amount: 0,
        sell_transactions: 0,
        sell_commission: 0,
        sell_amount: 0
    });
    const [graphData, setGraphData] = useState({
        labels: [],
        datasets: []
    });


    async function fetchSummaryData() {
        setLoadSpinner(true)
        setGraphLoading(true);
        let param = {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            group_by: groupBy
        }
        await viewCommissions(param).then((res) => {
            let tempres = res.body;
            setSummaryData(tempres.summary);

            setGraphData(tempres.graph);

            setLoadSpinner(false);
            setGraphLoading(false);
        }).catch(() => { setLoadSpinner(false); setGraphLoading(false); })
    }
    // Handle date range change
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateRange(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle group by change
    const handleGroupByChange = (e) => {
        setGroupBy(e.target.value);
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchSummaryData();
        // fetchGraphData();
    }, [dateRange, groupBy]);

    // Chart options
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                mode: 'index',  // Show tooltip for all datasets at the same index
                intersect: false,
                callbacks: {
                    label: function (context) {
                        // Only show individual dataset labels in the tooltip
                        return `${context.dataset.label}: $${context.raw.toFixed(2)}`;
                    },
                    afterBody: function (context) {
                        // Calculate totals for the stacked bar
                        const buy = context[0]?.raw || 0;
                        const sell = context[1]?.raw || 0;
                        const total = buy + sell;

                        if (total <= 0) return null;

                        return [
                            `Buy: ${((buy / total) * 100).toFixed(1)}%`,
                            `Sell: ${((sell / total) * 100).toFixed(1)}%`,
                            `Total: $${total.toFixed(2)}`
                        ];
                    },
                    // Remove the default title (date) if you want
                    title: function (context) {
                        return context[0].label;  // This will show the date/label
                    }
                }
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                grid: {
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                },
                title: {
                    display: true,
                    text: 'Commission Amount ($)'
                }
            },
        },
    };

    return (
        <Box sx={{ p: isMobile ? 1 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                {/* <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    Commission Earnings
                </Typography> */}

                <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            name="startDate"
                            value={dateRange.startDate}
                            onChange={handleDateChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size="small"
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            name="endDate"
                            value={dateRange.endDate}
                            onChange={handleDateChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            size="small"
                        />
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Group By</InputLabel>
                        <Select
                            value={groupBy}
                            label="Group By"
                            onChange={handleGroupByChange}
                        >
                            <MenuItem value="day">Daily</MenuItem>
                            <MenuItem value="week">Weekly</MenuItem>
                            <MenuItem value="month">Monthly</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {loadSpinner ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Card elevation={3} sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Receipt color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            Transactions
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                        {summaryData.total_number_transactions}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                        <Chip
                                            icon={<TrendingUp />}
                                            label={`Buy: ${summaryData.buy_transactions}`}
                                            color="success"
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={<TrendingDown />}
                                            label={`Sell: ${summaryData.sell_transactions}`}
                                            color="error"
                                            variant="outlined"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card elevation={3} sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <AttachMoney color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            Commission
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                        ${summaryData.total_commission_amount.toFixed(2)}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Buy Commission:
                                            </Typography>
                                            <Typography variant="body2" color="success.main">
                                                ${summaryData.buy_commission.toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Sell Commission:
                                            </Typography>
                                            <Typography variant="body2" color="error.main">
                                                ${summaryData.sell_commission.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Card elevation={3} sx={{ height: '100%' }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <AttachMoney color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            Transaction Volume
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                        ${summaryData.total_transaction_amount.toFixed(2)}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Buy Amount:
                                            </Typography>
                                            <Typography variant="body2">
                                                ${summaryData.buy_amount.toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Sell Amount:
                                            </Typography>
                                            <Typography variant="body2">
                                                ${summaryData.sell_amount.toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Card elevation={3} sx={{ mb: 4 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <BarChart color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Commission Trends (Buy vs Sell)</Typography>
                            </Box>
                            {graphLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', height: '400px', alignItems: 'center' }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box sx={{ height: isMobile ? '250px' : '400px' }}>
                                    <Bar options={chartOptions} data={graphData} />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};

export default CommissionDashboard;